import { useEffect, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/ChapterSlice";
import { RootState } from "src/redux/store";
import {
  useDeleteChapterMutation,
  useGetAllChapterWithPaginationQuery,
} from "src/services/ChapterServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import AddChapterFormWrapper from "../Add/AddChapterFormWrapper";
import ChapterListing from "./ChapterListing";
import { showToast } from "src/utils/showToaster";
import EditChapterFormWrapper from "../Edit/EditChapterFormWrapper";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const ChapterListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { totalItems, page, rowsPerPage, searchValue, isTableLoading, items } =
    useSelector((state: RootState) => state.chapter);
  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const [deleteChapter] = useDeleteChapterMutation();
  const { data, isLoading, isFetching } = useGetAllChapterWithPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["chapterName", "chapterContent", "duration"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  const columns: columnTypes[] = [
    {
      field: "chapterName",
      headerName: "Chapter Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <span className="py-2"> {row.chapterName} </span>
      ),
    },
    {
      field: "duration",
      headerName: "duration",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <span className="py-2"> {row.duration} minutes</span>
      ),
    },

    {
      noAuthRequired: true,
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  const getActionOptions = (chapterId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedId(chapterId);
          setIsOpenEditForm(true);
        },
        moduleName: UserModuleNameTypes.CHAPTERS,
        actionName: UserModuleActionTypes.EDIT,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Chapter ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                deleteChapter({ id: chapterId }).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        moduleName: UserModuleNameTypes.CHAPTERS,
        actionName: UserModuleActionTypes.DELETE,
      },
    ];
  };

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  return (
    <div>
      <ChapterListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.CHAPTERS,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddFrom ? (
        <AddChapterFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditChapterFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          chapterId={selectedId}
        />
      ) : null}
    </div>
  );
};

export default ChapterListingWrapper;
